import { useRef, type ChangeEvent, type ClipboardEvent, type KeyboardEvent } from 'react'
import Box from '@mui/material/Box'
import OutlinedInput from '@mui/material/OutlinedInput'
import { borderInput } from '@/theme/designTokens'
import {
  displayOtpCell,
  mergeOtpDigitAt,
  normalizeOtpCell,
  otpFromClipboard,
  stripNonDigits,
  stripOtpWhitespace,
} from './otpInputHelpers'

const OTP_LENGTH = 6

interface OtpInputProps {
  readonly value: string
  readonly onChange: (value: string) => void
  readonly disabled?: boolean
  readonly error?: boolean
  readonly id?: string
}

export function OtpInput({
  value,
  onChange,
  disabled = false,
  error = false,
  id = 'otp-code',
}: OtpInputProps) {
  const inputsRef = useRef<(HTMLInputElement | null)[]>([])
  const digits = value.padEnd(OTP_LENGTH, ' ').slice(0, OTP_LENGTH).split('')

  const setDigitAt = (index: number, char: string) => {
    const next = mergeOtpDigitAt(digits, index, char)
    onChange(stripOtpWhitespace(next.join('')).slice(0, OTP_LENGTH))
  }

  const handleChange =
    (index: number) => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const raw = stripNonDigits(e.target.value)
      if (!raw) {
        setDigitAt(index, '')
        return
      }
      const chars = raw.slice(0, OTP_LENGTH - index).split('')
      let combined = digits.map((d) => normalizeOtpCell(d)).join('')
      chars.forEach((c, offset) => {
        const pos = index + offset
        if (pos < OTP_LENGTH) {
          combined =
            combined.slice(0, pos) + c + combined.slice(pos + 1)
        }
      })
      onChange(combined.slice(0, OTP_LENGTH))
      const nextFocus = Math.min(index + chars.length, OTP_LENGTH - 1)
      inputsRef.current[nextFocus]?.focus()
    }

  const handleKeyDown =
    (index: number) => (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace' && !digits[index]?.trim()) {
        if (index > 0) {
          inputsRef.current[index - 1]?.focus()
        }
      }
    }

  const handlePaste = (e: ClipboardEvent<HTMLDivElement>) => {
    const pasted = otpFromClipboard(e.clipboardData.getData('text'), OTP_LENGTH)
    if (!pasted) return
    e.preventDefault()
    onChange(pasted)
    const focusIndex = Math.min(pasted.length, OTP_LENGTH) - 1
    inputsRef.current[focusIndex]?.focus()
  }

  return (
    <Box
      role="group"
      aria-label="6-digit verification code"
      data-testid="otp-input"
      sx={{ display: 'flex', gap: 1, justifyContent: 'center', width: '100%' }}
      onPaste={handlePaste}
    >
      {Array.from({ length: OTP_LENGTH }, (_, index) => (
        <OutlinedInput
          key={index}
          id={index === 0 ? id : undefined}
          inputRef={(el) => {
            inputsRef.current[index] = el
          }}
          value={displayOtpCell(digits[index])}
          onChange={handleChange(index)}
          onKeyDown={handleKeyDown(index)}
          disabled={disabled}
          error={error}
          inputProps={{
            maxLength: OTP_LENGTH,
            inputMode: 'numeric',
            pattern: '[0-9]*',
            'aria-label': `Digit ${index + 1} of ${OTP_LENGTH}`,
            style: { textAlign: 'center', padding: '12px 0', fontSize: '20px' },
          }}
          sx={{
            width: 48,
            height: 52,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: borderInput,
            },
          }}
        />
      ))}
    </Box>
  )
}
